import Region from "../Region";
import RegionArea from "../RegionArea";
import Phaser from 'phaser';

import * as ABCya from '@abcya/abcya_phaser';
import UIAssetKeys from '../UIAssetKeys';
import { AREAS, GameData, getFoundShapesInArea, REGIONS, SHAPES } from '../gameData';
import SoundAssetKeys from "../SoundAssetKeys";

export default class BeachballArea extends RegionArea
{
    /**
     * Create the beachball area.
     * @param {Region} region the owner region
     */
    constructor(parentRegion)
    {
        super(parentRegion, AREAS[REGIONS.BEACH].BEACHBALL);

        this._createArea();

        this.reset();
    }

    /**
     * Reset should be called when you want to set the state to a disabled/inactive state.
     */
    reset()
    {
        super.reset();

        this.beachballSphere.setAlpha(0);
        this.beachballSmallCircle.setAlpha(0);

        this.areaInformation[SHAPES.CIRCLE].tutorialArrow.setAlpha(0);
        this.tutorialArrow.setAlpha(0);
    }

    /**
     * Prepare should be called before you want to prepare the state to become active.
     */
    prepare()
    {
        super.prepare();

        this.beachballSphere.disableInteractive();
        this.beachballSmallCircle.disableInteractive();

        this.areaInformation[SHAPES.CIRCLE].tutorialArrow.setAlpha(0);
        this.tutorialArrow.setAlpha(0);

        this.parentRegion.scene.tweens.killTweensOf([this.tutorialArrow, this.areaInformation[SHAPES.CIRCLE].tutorialArrow]);
        this.areaInformation[SHAPES.CIRCLE].tutorialArrow.setPosition(312, 359);
        this.tutorialArrow.setPosition(588, 398);
    }

    /**
     * Send all game objects to their start positions.
     */
    _sendObjectsToStartPositions()
    {
        super._sendObjectsToStartPositions();

        this.beachballSphere.setAlpha(0.000001);
        this.beachballSmallCircle.setAlpha(0.000001);
    }

    /**
     * Creates the area, by adding the overlays to the parent region and creates the area information sidebar.
     */
    _createArea()
    {
        this.beachballSphere = ABCya.Images.addImage(709, 412, UIAssetKeys._05_Beach_Zoom_In_Beach_Ball_BchBll_Slct_Crcl, this.parentRegion);
        this.beachballSphere.setInteractive(new Phaser.Geom.Circle(this.beachballSphere.width / 2, this.beachballSphere.height / 2, this.beachballSphere.width / 2), Phaser.Geom.Circle.Contains);
        this.beachballSphere.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () => 
        {
            this._onOverlayPointerDown(this.beachballSphere, SHAPES.SPHERE);
        });

        this.beachballSmallCircle = ABCya.Images.addImage(665, 342, UIAssetKeys._05_Beach_Zoom_In_Beach_Ball_BchBll_Slct_SmCrcl, this.parentRegion);
        this.beachballSmallCircle.setInteractive(new Phaser.Geom.Circle(this.beachballSmallCircle.width / 2, this.beachballSmallCircle.height / 2, this.beachballSmallCircle.width / 2), Phaser.Geom.Circle.Contains);
        this.beachballSmallCircle.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () =>
        {
            this.parentRegion.scene.tweens.killTweensOf(this.tutorialArrow);
            ABCya.Tweens.addTween(this.tutorialArrow, 350, { alpha: 0 }, Phaser.Math.Easing.Quadratic.Out);
            // set the sphere shape button to be interactable if it wasn't already
            this.areaInformation[SHAPES.SPHERE].shapeButton.setInteractive();
            this._onOverlayPointerDown(this.beachballSmallCircle, SHAPES.CIRCLE);
        });

        this.tutorialArrow = ABCya.Images.addImage(588, 398, UIAssetKeys._Tutorial_Arw_Rt, this.parentRegion);

        this.shapeOverlays[SHAPES.CIRCLE] = [this.beachballSmallCircle];
        this.shapeOverlays[SHAPES.SPHERE] = [this.beachballSphere];

        this._createAreaInformation(0x5f5fd6, 0x5f5fd6);
    }

    /**
     * Callback function when an overlay is clicked on. Extends the original function and hides the area instructions.
     * @param {Phaser.GameObjects.Image} overlay the shape overlay that was clicked on
     * @param {string} shape the SHAPE enum value that the overlay belongs to
     */
    _onOverlayPointerDown(overlay, shape)
    {
        if (this.parentRegion.areaInstructionsPopup.state == 1)
        {
            this.parentRegion.areaInstructionsPopup.state = 2;
            ABCya.Tweens.addTween(this.parentRegion.areaInstructionsPopup, 350, { y: this.parentRegion.areaInstructionsPopup.offScreenPosition.y }, Phaser.Math.Easing.Back.In);
        }

        super._onOverlayPointerDown(overlay, shape);
    }

    /**
     * Creates the shape information for the area. These are the buttons that can be clicked to find the shapes.
     * Extends the original function and adds an additional listener to show the 2nd set of instructions when a shape name is clicked.
     * @param {number} numberFontColor the number font color
     * @param {number} textFontColor the text font color
     */
    _createAreaInformation(numberFontColor, textFontColor)
    {
        super._createAreaInformation(numberFontColor, textFontColor);

        for (let shape in GameData.region_data[this.region][this.area])
        {
            let containerShapeObj = this.areaInformation[shape];
            containerShapeObj.shapeButton.on(Phaser.Input.Events.GAMEOBJECT_POINTER_DOWN, () => 
            {
                this.parentRegion.scene.tweens.killTweensOf(this.areaInformation[SHAPES.CIRCLE].tutorialArrow);
                ABCya.Tweens.addTween(this.areaInformation[SHAPES.CIRCLE].tutorialArrow, 350, { alpha: 0 }, Phaser.Math.Easing.Quadratic.Out);

                if (this.parentRegion.areaInstructionsPopup.state == 0)
                {
                    this.parentRegion.areaInstructionsPopup.state = 1;
                    if (this.parentRegion.areaInstructionsPopup.firstInstructionsAudio.isPlaying)
                    {
                        this.parentRegion.areaInstructionsPopup.firstInstructionsAudio.stop();
                    }
                    ABCya.Audio.playOneShot(SoundAssetKeys._locate_that_shape, this.parentRegion.scene, 1, false, 0.25);
                    ABCya.Tweens.addTween(this.parentRegion.areaInstructionsPopup.firstInstructions, 350, { alpha: 0 }, Phaser.Math.Easing.Quadratic.In);
                    ABCya.Tweens.addTween(this.parentRegion.areaInstructionsPopup.secondInstructions, 350, { alpha: 1, delay: 300 }, Phaser.Math.Easing.Quadratic.Out);
                    ABCya.Tweens.addTween(this.tutorialArrow, 350, { alpha: 1 }, Phaser.Math.Easing.Quadratic.Out);
                    ABCya.Tweens.addTween(this.tutorialArrow, 1000,
                        {
                            x: this.tutorialArrow.x + 10,
                            y: this.tutorialArrow.y - 8,
                            yoyo: true,
                            repeat: -1,
                            repeatDelay: 250
                        },
                        Phaser.Math.Easing.Quadratic.InOut);
                }
            });
        }

        this.areaInformation[SHAPES.CIRCLE].tutorialArrow = ABCya.Images.addImage(312, 359, UIAssetKeys._Tutorial_Arw_Lft, this.areaInformation);
    }

    /**
     * Zoom into the area.
     */
    zoomIn()
    {
        let tweens = super.zoomIn();

        tweens.push(
            {
                targets: [
                    this.parentRegion.sandcastle, this.parentRegion.lifeguardContainer, this.parentRegion.seagullContainer
                ],
                duration: 500,
                props: { alpha: 0 },
                ease: Phaser.Math.Easing.Quadratic.Out,
            },
            { targets: this.parentRegion.beachball, duration: 1000, props: { scale: 1, x: 709, y: 411 }, ease: Phaser.Math.Easing.Quadratic.Out, offset: 0 },
            { targets: this.areaInformation, duration: 500, props: { x: this.areaInformation.onScreenPosition.x }, ease: Phaser.Math.Easing.Back.Out, offset: 500 },
        );

        if (getFoundShapesInArea(this.parentRegion.region, this.area) == 0)
        {
            tweens.push({ targets: this.areaInformation[SHAPES.CIRCLE].tutorialArrow, duration: 350, props: { alpha: 1 }, ease: Phaser.Math.Easing.Quadratic.Out, offset: 1000 });
        }

        return tweens;
    }

    /**
     * Zoom out of the area.
     */
    zoomOut()
    {
        let tweens = super.zoomOut();

        this.beachballSphere.disableInteractive();
        this.beachballSmallCircle.disableInteractive();

        for (let shape in this.shapeOverlays)
        {
            // fade off the overlay
            tweens.push(
                {
                    targets: this.shapeOverlays[shape],
                    duration: 500,
                    props: { alpha: 0 },
                    ease: Phaser.Math.Easing.Quadratic.Out,
                    offset: 0
                }
            );
        }

        tweens.push(
            { targets: this.areaInformation, duration: 500, props: { x: this.areaInformation.offScreenPosition.x }, ease: Phaser.Math.Easing.Back.In, offset: 0 },
            { targets: this.parentRegion.beachball, duration: 1000, props: { scale: 0.15, x: this.parentRegion.beachball.onScreenPosition.x, y: this.parentRegion.beachball.onScreenPosition.y }, ease: Phaser.Math.Easing.Quadratic.Out, offset: 250 },
            { targets: [this.parentRegion.lifeguardContainer, this.parentRegion.seagullContainer, this.parentRegion.sandcastle], duration: 500, props: { alpha: 1 }, ease: Phaser.Math.Easing.Quadratic.Out, offset: 500 },
            { targets: this.areaInformation[SHAPES.CIRCLE].tutorialArrow, duration: 350, props: { alpha: 0 }, ease: Phaser.Math.Easing.Quadratic.Out, offset: 0 },
            { targets: this.tutorialArrow, duration: 350, props: { alpha: 0 }, ease: Phaser.Math.Easing.Quadratic.Out, offset: 0 }
        );

        return tweens;
    }

    /**
     * Sets the shape buttons to be interactable or not. Override to set only the circle button active during tutorial.
     * @param {Boolean} isInteractable whether the shape buttons should be interactable.
     */
    setAreaShapeButtonInteractable(isInteractable)
    {
        if (!isInteractable || getFoundShapesInArea(this.parentRegion.region, this.area) > 0)
        {
            // if we're setting everything to not be interactable, use the original function
            // if shapes have been found in this area already, use the original function
            super.setAreaShapeButtonInteractable(isInteractable);
        }
        else
        {
            // only activate the circle shape during the tutorial
            this.areaInformation[SHAPES.CIRCLE].shapeButton.setInteractive();

            ABCya.Tweens.addTween(this.areaInformation[SHAPES.CIRCLE].tutorialArrow, 1000,
                {
                    x: this.areaInformation[SHAPES.CIRCLE].tutorialArrow.x - 10,
                    y: this.areaInformation[SHAPES.CIRCLE].tutorialArrow.y - 8,
                    yoyo: true,
                    repeat: -1,
                    repeatDelay: 250
                },
                Phaser.Math.Easing.Quadratic.InOut);
        }
    }
}